import {ReactNode, useCallback, useRef} from 'react';
import {view} from '@sail/ui/deprecated';
import {
  Conversation,
  InlineDataResult as ConversationsClientInlineDataResult,
  InlineDataResultAction,
  InlineDataConversationMessage,
  Action,
  StripeAssistantCommandResponse,
  StripeAssistantLiveAnswersResponse,
  ClientResponse,
} from '@stripe-internal/conversations-client';
import {
  InlineDataProps,
  InlineDataResult,
  InlineDataServiceContext,
} from '@stripe-internal/inline-data';
import {InlineDataRow} from '../../../Chat/Row/InlineDataRow';
import {StripeAssistantRow} from '../../../Chat/Row/StripeAssistantRow';
import {LiveAnswersRow} from '../../../Chat/Row/LiveAnswersRow';
import {SupportSiteEscalation} from '../../../StripeAssistant/SupportSiteEscalation';
import {useAssistantAnalyticsCall} from '../../../StripeAssistant/analytics-utils';
import {useAnalytics} from '../../../AnalyticsProvider';

type Props = {
  conversation: Conversation;
  message: InlineDataConversationMessage;
  children?: ReactNode;
  avatar?: ReactNode | null;
};

export const InlineDataMessageRow = ({
  conversation,
  message,
  avatar,
  children,
}: Props) => {
  const analytics = useAnalytics();
  const inlineData = message.inlineData as InlineDataProps;
  const stripeAssistantDoneRef = useRef<boolean>(false);

  const onDoneWithResponse = useCallback(
    (result: InlineDataResult): Promise<ClientResponse> => {
      const action: InlineDataResultAction = {
        name: 'inline-data-result',
        message,
        result: result as ConversationsClientInlineDataResult,
      };
      return conversation.action(action);
    },
    [conversation, message],
  );

  const onDone = useCallback(
    (result: InlineDataResult) => {
      onDoneWithResponse(result);
    },
    [onDoneWithResponse],
  );

  const stripeAssistantOnDone = useCallback(() => {
    if (!stripeAssistantDoneRef.current) {
      stripeAssistantDoneRef.current = true;
      onDoneWithResponse({type: 'success'})
        .then((resp) => {
          if (resp.code >= 400) {
            stripeAssistantDoneRef.current = false;
          }
        })
        .catch(() => {
          stripeAssistantDoneRef.current = false;
        });
    }
  }, [onDoneWithResponse]);

  useAssistantAnalyticsCall(conversation, message, (query: string | null) => {
    let classification = null;
    if (inlineData.name === 'live_answers_response') {
      classification = 'Summarize Documentation';
    } else if (
      inlineData.name === 'stripe_assistant' &&
      'classification' in inlineData.data &&
      !!inlineData.data.classification
    ) {
      classification = inlineData.data.classification;
    }

    if (classification) {
      analytics.action('ai_assistant_classify_intent_complete', {
        ai_assistant_question: query,
        ai_assistant_intent_classification: classification,
        ai_assistant_source: 'assistant',
      });
    }
  });

  // until we have a sanctioned path for stripe assistant messages,
  // we're overloading inline data messages. we shortcut here and
  // return an appropriate stripe assistant row
  if (inlineData.name === 'stripe_assistant') {
    const type = 'type' in inlineData.data ? inlineData.data.type : null;

    switch (type) {
      case 'live_answers_v2':
        return (
          <LiveAnswersRow
            conversation={conversation}
            messageId={message.id}
            data={
              inlineData.data as unknown as StripeAssistantLiveAnswersResponse
            }
            isDone={inlineData.isDone}
            onDone={stripeAssistantOnDone}
          />
        );

      case 'analyze_data': {
        const requestAction = (action: Action) => {
          conversation.action({
            name: 'request-action',
            action,
          });
        };
        return (
          <view.div data-testid="stripe-assistant-message-row">
            <StripeAssistantRow
              conversation={conversation}
              message={message}
              requestAction={requestAction}
              stripeAssistantResponse={
                inlineData.data as unknown as StripeAssistantCommandResponse
              }
            />
          </view.div>
        );
      }

      case 'link_to_support':
        return (
          <view.div data-testid="stripe-assistant-message-row">
            <SupportSiteEscalation
              conversation={conversation}
              message={message}
            />
          </view.div>
        );

      case 'unanswerable':
        return (
          // eslint-disable-next-line react/no-unescaped-entities
          <view.div>I couldn't answer that. Try another question.</view.div>
        );

      default:
        return <view.div>Something went wrong. Please try again.</view.div>;
    }
  }

  const background =
    message.participant.id === conversation.user.id
      ? 'neutral600'
      : 'neutral100';

  const position =
    message.participant.id === conversation.user.id ? 'end' : 'start';

  const {style} = message.inlineData;
  const serviceContext: InlineDataServiceContext = {
    conversation_id: conversation.id,
    effect_id: message.effectId,
  };

  return (
    <view.div
      data-testid="inline-data-message-row"
      className="uh-conversations-inline-data-message-row"
      data-message-type={message.participant.role}
    >
      <InlineDataRow
        inlineData={{...inlineData, context: serviceContext, onDone}}
        background={background}
        position={position}
        avatar={avatar}
        style={style}
      >
        {children}
      </InlineDataRow>
    </view.div>
  );
};
